import React, { useEffect, useMemo, useState } from "react";
import { useSpecialistProfile } from "../state/SpecialistProfileContext";

type HospitalRow = {
  HospitalID: number;
  Name: string | null;
};

type ProcedureRow = {
  ProcedureID: number;
  Name: string | null;
};

const TabHospitalsProcedures: React.FC = () => {
  const {
    formState,
    updateHospitalsProceduresField,
    hospitalsProceduresErrors,
  } = useSpecialistProfile();

  const { hospitalIds, hasNoHospitalPrivileges, procedureIds } =
    formState.hospitalsAndProcedures;

  const [hospitals, setHospitals] = useState<HospitalRow[]>([]);
  const [procedures, setProcedures] = useState<ProcedureRow[]>([]);

  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [loadingProcedures, setLoadingProcedures] = useState(false);

  const [errorHospitals, setErrorHospitals] = useState<string>("");
  const [errorProcedures, setErrorProcedures] = useState<string>("");

  const fetchJsonArray = async (url: string) => {
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed (status ${res.status})`);
    }
    const data = await res.json();
    if (!Array.isArray(data)) {
      throw new Error(`Unexpected response format from ${url}`);
    }
    return data;
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // Hospitals
      setLoadingHospitals(true);
      setErrorHospitals("");
      try {
        const data = await fetchJsonArray("/api/hospitals");
        if (!cancelled) setHospitals(data);
      } catch (e: any) {
        if (!cancelled) {
          setHospitals([]);
          setErrorHospitals(e?.message || "Failed to load hospitals.");
        }
      } finally {
        if (!cancelled) setLoadingHospitals(false);
      }

      // Procedures
      setLoadingProcedures(true);
      setErrorProcedures("");
      try {
        const data = await fetchJsonArray("/api/procedures");
        if (!cancelled) setProcedures(data);
      } catch (e: any) {
        if (!cancelled) {
          setProcedures([]);
          setErrorProcedures(e?.message || "Failed to load procedures.");
        }
      } finally {
        if (!cancelled) setLoadingProcedures(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const hospitalOptions = useMemo(() => {
    return hospitals
      .filter((h) => typeof h?.HospitalID === "number")
      .map((h) => ({
        id: String(h.HospitalID),
        name: h.Name && h.Name.trim() ? h.Name.trim() : `Hospital ${h.HospitalID}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [hospitals]);

  const procedureOptions = useMemo(() => {
    return procedures
      .filter((p) => typeof p?.ProcedureID === "number")
      .map((p) => ({
        id: String(p.ProcedureID),
        name: p.Name && p.Name.trim() ? p.Name.trim() : `Procedure ${p.ProcedureID}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [procedures]);

  const toggleMulti = (
    field: "hospitalIds" | "procedureIds",
    id: string
  ) => {
    const current = (formState.hospitalsAndProcedures as any)[field] as string[];
    const safeCurrent = Array.isArray(current) ? current : [];
    const exists = safeCurrent.includes(id);
    const updated = exists ? safeCurrent.filter((x) => x !== id) : [...safeCurrent, id];
    updateHospitalsProceduresField(field, updated);
  };

  const sectionBoxStyle: React.CSSProperties = {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 12,
    background: "#fafafa",
  };

  const hospitalDisabled = hasNoHospitalPrivileges === true;

  return (
    <div>
      <p style={{ marginBottom: 16 }}>
        Add hospital privileges and procedures offered. If the specialist has no
        hospital privileges, check the box below.
      </p>

      {/* No hospital privileges */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={!!hasNoHospitalPrivileges}
            onChange={(e) => {
              const checked = e.target.checked;
              updateHospitalsProceduresField("hasNoHospitalPrivileges", checked);

              // If user sets "no privileges", clear selected hospitals to avoid conflicts
              if (checked) {
                updateHospitalsProceduresField("hospitalIds", []);
              }
            }}
          />
          <span>No hospital privileges</span>
        </label>
      </div>

      {/* Hospitals */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 6 }}>
          Hospitals <span style={{ color: "red" }}>*</span>
        </label>

        <div style={sectionBoxStyle}>
          {loadingHospitals ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>Loading hospitals…</div>
          ) : errorHospitals ? (
            <div style={{ color: "red" }}>
              Could not load hospitals: {errorHospitals}
            </div>
          ) : hospitalOptions.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>No hospitals found in DB.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {hospitalOptions.map((opt) => {
                const checked = (hospitalIds || []).includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      borderRadius: 16,
                      border: checked ? "2px solid #0017a2" : "1px solid #ccc",
                      backgroundColor: checked ? "#eef1ff" : "#fff",
                      cursor: hospitalDisabled ? "not-allowed" : "pointer",
                      fontSize: 14,
                      opacity: hospitalDisabled ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={hospitalDisabled}
                      onChange={() => toggleMulti("hospitalIds", opt.id)}
                    />
                    <span>{opt.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {hospitalsProceduresErrors.hospitalIds && (
          <div style={{ color: "red", marginTop: 6 }}>
            {hospitalsProceduresErrors.hospitalIds}
          </div>
        )}

        {hospitalDisabled && (
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
            Hospitals are disabled because “No hospital privileges” is selected.
          </div>
        )}
      </div>

      {/* Procedures */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 6 }}>
          Procedures offered (optional)
        </label>

        <div style={sectionBoxStyle}>
          {loadingProcedures ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>Loading procedures…</div>
          ) : errorProcedures ? (
            <div style={{ color: "red" }}>
              Could not load procedures: {errorProcedures}
            </div>
          ) : procedureOptions.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>No procedures found in DB.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {procedureOptions.map((opt) => {
                const checked = (procedureIds || []).includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      borderRadius: 16,
                      border: checked ? "2px solid #0017a2" : "1px solid #ccc",
                      backgroundColor: checked ? "#eef1ff" : "#fff",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMulti("procedureIds", opt.id)}
                    />
                    <span>{opt.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TabHospitalsProcedures;
